import { useCallback, useMemo } from 'react';
import DocumentTitle from 'react-document-title';
import { useNavigate } from 'react-router-dom';

import { Box } from '@hivebrite/design-system/Box';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import BlocksRenderer from 'hb-react/frontoffice/components/BlocksRenderer';
import { ITEMS_QUERY_KEYS } from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/utils/queryUtils';
import { BLOCKS } from 'hb-react/frontoffice/components/BlocksRenderer/shared';
import oneColumnTemplate from 'hb-react/frontoffice/components/BlocksRenderer/templates/OneColumnTemplate';
import twoColumnsTemplate from 'hb-react/frontoffice/components/BlocksRenderer/templates/TwoColumnsTemplate';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import DonationsCardLoader from 'hb-react/shared/apps/Donations/components/DonationsCardLoader';
import {
  ITEMS_BY_ROW_ONE_COL,
  ITEMS_BY_ROW_TWO_COL,
  makeFetchItems,
  makeItemRenderer,
} from 'hb-react/shared/apps/Donations/utils/rendererUtils';
import { fetchCategories } from 'hb-react/shared/apps/Donations/utils/request';
import getPageCustomizableHeaderProps from 'hb-react/shared/apps/PageCustomizables/utils/getPageCustomizableHeaderProps';
import { useDonationContext } from 'hb-react/shared/contexts/DonationContext';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';

const TEMPLATES = {
  [oneColumnTemplate.id]: oneColumnTemplate,
  [twoColumnsTemplate.id]: twoColumnsTemplate,
};

const DonationsIndexContainer = ({ configPage, pageTitle }) => {
  const { whitelabelURL } = useDonationContext();
  const navigate = useNavigate();
  const { currentUser, currentNetwork } = useDefaultAppContexts();
  const breakpoint = useBreakpoint();

  const {
    advanced_options_enabled: advancedOptionsEnabled,
    mid_tier_enabled: midTierEnabled,
  } = currentNetwork.features.donations.config;

  const onDataFetched = useCallback((data) => {
    // To match the check in app/controllers/web/donations_controller.rb#index
    if (!(advancedOptionsEnabled || midTierEnabled) && data?.length > 0) {
      navigate(`${data[0].id}`, { replace: true });
    }
  }, []);

  // Never enable whitelabelURL if the user is connected
  const whitelabelURLEnabled = currentUser ? false : whitelabelURL;

  const appHeaderProps = getPageCustomizableHeaderProps({
    appPageConfig: configPage,
  });

  const itemRenderer = useMemo(
    () =>
      makeItemRenderer({
        whitelabelURLEnabled,
        navigate,
      }),
    [whitelabelURLEnabled, navigate],
  );

  const donationsPath = whitelabelURLEnabled
    ? '?format=json'
    : Routes.donations_path({ format: 'json' });

  const fetchItems = useMemo(
    () => makeFetchItems(donationsPath),
    [donationsPath],
  );

  const templateId =
    configPage?.config?.blocks_renderer_template || oneColumnTemplate.id;

  const template = TEMPLATES[templateId];

  return (
    <>
      <DocumentTitle
        title={generatePageTitle({
          title:
            pageTitle || configPage.menu_name || s__('Donations|Campaigns'),
          networkName: getPageTitleFromNetwork(currentNetwork),
        })}
      />
      <BlocksRenderer
        templateId={template.id}
        templateComponent={template.component}
        templateZones={template.zones}
        templateProps={{
          hasFilters: false,
          offsetRightZone: true, // TwoColumnsTemplate-specific
        }}
        blocks={[
          {
            ...BLOCKS.appHeader,
            attributes: {
              ...appHeaderProps,
            },
          },

          {
            ...BLOCKS.tags,
            attributes: {
              queryId: ITEMS_QUERY_KEYS.donations.tags,
              multi: true,
              fetchTags: () => fetchCategories(donationsPath),
            },
            templateAttributes: {
              [twoColumnsTemplate.id]: {
                label: s__('Donations|Filter by category'),
                limit: 15,
              },
            },
          },
          {
            id: ITEMS_QUERY_KEYS.donations.items,
            component: BLOCKS.items.component,
            attributes: {
              fetchItems,
              itemLoader: DonationsCardLoader,
              itemRenderer,
              title: ({ itemsSize, isLoading }) => (
                <Box
                  font="h2-semibold"
                  as="span"
                  mb={-3}
                  mt={templateId === oneColumnTemplate.id ? -2 : 2}
                >
                  {s__('Donations|Campaigns')} {!isLoading && `(${itemsSize})`}
                </Box>
              ),
              setBlockData: onDataFetched,
              columnsInMobile: true,
            },
            templateAttributes: {
              [oneColumnTemplate.id]: {
                itemsByRow: ITEMS_BY_ROW_ONE_COL[breakpoint],
              },
              [twoColumnsTemplate.id]: {
                itemsByRow: ITEMS_BY_ROW_TWO_COL[breakpoint],
              },
            },
          },
        ]}
      />
    </>
  );
};

export default DonationsIndexContainer;
